import React, { useEffect } from 'react';
import { styled } from '@compiled/react';
import { useAnalyticsEvents, type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import BookIcon from '@atlaskit/icon/glyph/book';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import EditorEditIcon from '@atlaskit/icon/glyph/editor/edit';
import HipchatLobbyIcon from '@atlaskit/icon/glyph/hipchat/lobby';
import LinkIcon from '@atlaskit/icon/glyph/link';
import QuestionsIcon from '@atlaskit/icon/glyph/questions';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { Skeleton } from '@atlassian/jira-common-components-skeleton/src/styled.tsx';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { ExternalLinkObject, ExternalLinkObjectType } from '../../../common/types';
import { useLinkIcon } from '../../../controllers/store/index.tsx';
import messages from './messages';

export type LinkProps = ExternalLinkObject & {
	index: number;
	onDelete: (id: string) => void;
	onUpdate: (id: string) => void;
};

export function Link(props: LinkProps) {
	const { id, title, description, link, resourceType, index, onDelete, onUpdate } = props;
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	let origin: string;
	try {
		origin = new URL(link).origin;
	} catch (error) {
		origin = '';
	}
	const isValidOrigin = Boolean(origin) && origin !== 'null';
	const [data, { fetchExternalLinkIcon }] = useLinkIcon(origin);

	useEffect(() => {
		if (isValidOrigin) {
			fetchExternalLinkIcon(origin);
		}
	}, [isValidOrigin, origin, fetchExternalLinkIcon]);

	const getFallbackIcon = (iconType: ExternalLinkObjectType) => {
		switch (iconType) {
			case 'request-form': {
				return (
					<HipchatLobbyIcon label={formatMessage(messages.externalLinkRequestFormIconLabel)} />
				);
			}
			case 'knowledge': {
				return <BookIcon label={formatMessage(messages.externalLinkKnowledgeIconLabel)} />;
			}
			case 'channel': {
				return <QuestionsIcon label={formatMessage(messages.externalLinkChannelIconLabel)} />;
			}
			default: {
				return <LinkIcon label={formatMessage(messages.externalLinkOtherIconLabel)} />;
			}
		}
	};

	const linkAnalyticsEvent: UIAnalyticsEvent = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'link',
	});

	const getIcon = () => {
		if (isValidOrigin) {
			if (!data || data.isLoading) {
				return <Skeleton height={iconDimension} width={iconDimension} />;
			}

			if (data.iconUrl) {
				return <ImageContainer src={data.iconUrl} alt="" />;
			}
		}

		return getFallbackIcon(resourceType);
	};

	return (
		<LinkWrapper>
			<Box xcss={iconWrapperStyles}>{getIcon()}</Box>
			<ContentWrapper>
				<Tooltip
					content={formatMessage(messages.externalLinkTitleTooltip)}
					position="mouse"
					mousePosition="top"
				>
					<LinkContainer
						target="_blank"
						rel="noopener noreferrer"
						href={link}
						onClick={() => {
							fireUIAnalytics(linkAnalyticsEvent, 'externalResource', {
								link_position: index,
								resourceType,
							});
						}}
						data-testid="servicedesk-external-links.ui.links.link.a"
					>
						<Heading size="small" as="h2">
							{title}
						</Heading>
					</LinkContainer>
				</Tooltip>
				<DescriptionContainer>{description}</DescriptionContainer>
			</ContentWrapper>
			<LinkActions>
				<Tooltip content={formatMessage(messages.externalLinkUpdateButtonTooltip)}>
					<Button
						appearance="subtle"
						name="external-resource-update"
						iconBefore={
							<EditorEditIcon
								label={formatMessage(messages.externalLinkUpdateButtonLabel, {
									title,
								})}
							/>
						}
						onClick={(_, analyticsEvent) => {
							fireUIAnalytics(analyticsEvent, 'externalResourceUpdate', {
								link_position: index + 1,
								resourceType,
							});
							onUpdate(id);
						}}
						data-testId="servicedesk-external-links.ui.links.link.update-link-button"
					/>
				</Tooltip>
				<Tooltip content={formatMessage(messages.externalLinkRemoveButtonTooltip)}>
					<Button
						appearance="subtle"
						name="external-resource-delete"
						iconBefore={
							<CrossIcon
								label={formatMessage(messages.externalLinkRemoveButtonLabel, {
									title,
								})}
							/>
						}
						onClick={(_, analyticsEvent) => {
							fireUIAnalytics(analyticsEvent, 'externalResourceDelete', {
								link_position: index + 1,
								resourceType,
							});
							onDelete(id);
						}}
						data-testId="servicedesk-external-links.ui.links.link.delete-link-button"
					/>
				</Tooltip>
			</LinkActions>
		</LinkWrapper>
	);
}

export default Link;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LinkWrapper = styled.div({
	display: 'flex',
	cursor: 'default',
	padding: token('space.050', '4px'),
	marginBottom: token('space.200', '16px'),
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LinkActions = styled.div({
	display: 'flex',
	alignSelf: 'center',
	marginRight: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	flex: 1,
	wordBreak: 'break-all',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DescriptionContainer = styled.p({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N400),
});

const iconWrapperStyles = xcss({
	marginRight: 'space.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const iconDimension = `${3 * gridSize}px`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ImageContainer = styled.img({
	height: iconDimension,
	width: iconDimension,
	objectFit: 'contain',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LinkContainer = styled.a({
	display: 'inline-block',

	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: token('color.text', colors.N800),
	},
});
